import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import type { InputDevice } from '@/input/types';
import { arrowGlyph, crossGlyph, cycleGlyph } from './hangar-icons';
import { hexPath, label } from './widgets';

/** What the confirm button does on the focused item (footer dock). */
export type PromptAction =
  | { kind: 'buy'; price: number; red: boolean }
  | { kind: 'equip'; socket: number }
  | { kind: 'pin'; socket: number }
  | { kind: 'reroll'; price: number }
  | { kind: 'use' }
  | { kind: 'continue' }
  | { kind: 'none' };

export type PadButton = 'confirm' | 'scrap' | 'info' | 'reroll';

const CAP = 24;
const PAD: Record<PadButton, { glyph: string; color: number; key: string }> = {
  confirm: { glyph: 'A', color: 0x4ad07a, key: '␣' },
  scrap: { glyph: 'X', color: 0x3d7be0, key: 'X' },
  info: { glyph: 'Y', color: 0xf5c542, key: 'I' },
  reroll: { glyph: 'B', color: 0xe8544c, key: 'R' },
};

/** Device key cap: Ⓐ/Ⓧ/Ⓨ/Ⓑ circle, keyboard key, or a touch tap ring. */
export function keyCap(device: InputDevice, button: PadButton): Container {
  const c = new Container();
  const g = new Graphics();
  const p = PAD[button];
  if (device === 'touch') {
    g.circle(0, 0, CAP / 2).stroke({ color: 0xe8eaf0, width: 2 });
    g.circle(0, 0, 4).fill(0xe8eaf0);
    c.addChild(g);
    return c;
  }
  if (device === 'gamepad') {
    g.circle(0, 0, CAP / 2).fill(p.color);
  } else {
    const w = button === 'confirm' ? CAP * 1.7 : CAP;
    g.roundRect(-w / 2, -CAP / 2, w, CAP, 5).fill(0x2a2440).stroke({ color: 0x8a9099, width: 2 });
  }
  c.addChild(g);
  const txt = label(device === 'gamepad' ? p.glyph : p.key, 14, device === 'gamepad' ? 0x0f0c1a : 0xe8eaf0, 0);
  txt.anchor.set(0.5);
  txt.position.set(0, 1);
  c.addChild(txt);
  return c;
}

/** Wordless action icon for the primary prompt. */
function actionIcon(a: PromptAction): Container {
  const c = new Container();
  const g = new Graphics();
  c.addChild(g);
  switch (a.kind) {
    case 'buy': {
      const gem = new Sprite(getTexture(a.red ? 'pickup/gem-red' : 'pickup/gem-yellow'));
      gem.anchor.set(0, 0.5);
      gem.scale.set(0.5);
      const n = label(String(a.price), 16, a.red ? COLORS.gemRed : COLORS.gemYellow, 1);
      n.anchor.set(0, 0.5);
      n.position.set(22, 0);
      c.addChild(gem, n);
      break;
    }
    case 'equip':
    case 'pin': {
      // equip: → [n]   ·   pin: just the yellow [n] (aim at this socket)
      const hx = a.kind === 'equip' ? 34 : 12;
      if (a.kind === 'equip') arrowGlyph(g, 10, 0, 9, 0xe8eaf0);
      hexPath(g, hx, 0, 12).fill(COLORS.playerYellow);
      const n = label(String(a.socket + 1), 14, 0x0f0c1a, 0);
      n.anchor.set(0.5);
      n.position.set(hx, 1);
      c.addChild(n);
      break;
    }
    case 'reroll': {
      cycleGlyph(g, 10, 0, 9, COLORS.uiAccent);
      const gem = new Sprite(getTexture('pickup/gem-yellow'));
      gem.anchor.set(0, 0.5);
      gem.scale.set(0.5);
      gem.position.set(26, 0);
      const n = label(String(a.price), 16, COLORS.gemYellow, 1);
      n.anchor.set(0, 0.5);
      n.position.set(48, 0);
      c.addChild(gem, n);
      break;
    }
    case 'continue':
      g.poly([2, -10, 20, 0, 2, 10]).fill(COLORS.uiAccent);
      break;
    case 'use': // check mark: apply a stored plate
      g.moveTo(2, 0).lineTo(8, 7).lineTo(20, -8).stroke({ color: COLORS.statUp, width: 4, cap: 'round', join: 'round' });
      break;
    case 'none':
      break;
  }
  return c;
}

/**
 * Footer dock (docs/09 wave 4): ONE highlighted primary action for the
 * focused item — key cap + what it does — then the secondary buttons that
 * apply right now. Glyphs only, never words.
 */
export function buildPromptDock(
  device: InputDevice, primary: PromptAction,
  secondary: { scrap: boolean; scrapValue: number; info: boolean; reroll: boolean },
): Container {
  const c = new Container();
  let x = 0;
  if (primary.kind !== 'none') {
    const bg = new Graphics();
    const cap = keyCap(device, 'confirm');
    cap.position.set(x + 22, 0);
    const icon = actionIcon(primary);
    icon.position.set(x + 46, 0);
    const w = 46 + icon.width + 16;
    bg.roundRect(x, -16, w, 32, 8).fill({ color: 0x1d4a44, alpha: 0.9 }).stroke({ color: COLORS.uiAccent, width: 2 });
    c.addChild(bg, cap, icon);
    x += w + 18;
  }
  if (device === 'touch') return c;
  const pair = (button: PadButton, draw: (g: Graphics, ox: number) => number) => {
    const cap = keyCap(device, button);
    cap.position.set(x + CAP / 2 + (button === 'confirm' ? 8 : 0), 0);
    c.addChild(cap);
    const g = new Graphics();
    c.addChild(g);
    x += CAP + 8 + draw(g, x + CAP + 8) + 18;
  };
  if (secondary.scrap) {
    pair('scrap', (g, ox) => {
      crossGlyph(g, ox + 6, 0, 5, COLORS.dangerRed);
      const gem = new Sprite(getTexture('pickup/gem-yellow'));
      gem.anchor.set(0, 0.5);
      gem.scale.set(0.42);
      gem.position.set(ox + 16, 0);
      const n = label(`+${secondary.scrapValue}`, 14, COLORS.gemYellow, 1);
      n.anchor.set(0, 0.5);
      n.position.set(ox + 34, 0);
      c.addChild(gem, n);
      return 34 + n.width;
    });
  }
  if (secondary.info) {
    pair('info', (g, ox) => {
      g.circle(ox + 9, 0, 9).stroke({ color: 0xe8eaf0, width: 2 });
      g.circle(ox + 9, -4, 1.6).fill(0xe8eaf0);
      g.roundRect(ox + 7.8, -1, 2.4, 7, 1).fill(0xe8eaf0);
      return 18;
    });
  }
  if (secondary.reroll) {
    pair('reroll', (g, ox) => {
      cycleGlyph(g, ox + 9, 0, 8, COLORS.uiAccent);
      return 20;
    });
  }
  return c;
}
